import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, Loader2, Save, Activity } from "lucide-react";
import { getSettings, updateSettings, getLogs } from "../../../services/dataService";
import { PageHeader } from "../../PageHeader";
import { StatusBadge } from "../../StatusBadge";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Switch } from "../../ui/switch";
import { formatApiErrorDetail } from "../../../lib/api";
import { formatDateTime } from "../../../lib/format";
import { toast } from "sonner";

const TOGGLES = [
  { name: "notify_on_sync_success", label: "Xero sync success", desc: "Notify when invoices are pulled from Xero." },
  { name: "notify_on_sync_failure", label: "Xero sync failure", desc: "Alert when a Xero sync fails or a token expires." },
  { name: "notify_on_batch_sent", label: "Batch sent to Netcash", desc: "Confirm when a batch is submitted to Netcash." },
  { name: "notify_on_bounce", label: "Debit order bounced", desc: "Alert when a debit order bounces or fails." },
];

export function NotificationSettings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["settings", "notifications"], queryFn: () => getSettings("notifications") });
  const { data: logs = [] } = useQuery({ queryKey: ["logs"], queryFn: getLogs });
  const [form, setForm] = useState(null);

  useEffect(() => { if (data) setForm(data); }, [data]);

  const mut = useMutation({
    mutationFn: (payload) => updateSettings("notifications", payload),
    onSuccess: (res) => { qc.setQueryData(["settings", "notifications"], res); setForm(res); toast.success("Preferences saved"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"),
  });

  if (isLoading || !form) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  return (
    <div>
      <PageHeader title="Notification & Logging Preferences" icon={Bell} description="Control alerts for API sync success/failure and review the activity & logging feed." />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Preferences */}
        <div className="lg:col-span-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm space-y-5" data-testid="notifications-form">
          {TOGGLES.map((t) => (
            <div key={t.name} className="flex items-start justify-between gap-4">
              <div>
                <Label className="font-semibold">{t.label}</Label>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t.desc}</p>
              </div>
              <Switch checked={!!form[t.name]} onCheckedChange={(v) => setForm({ ...form, [t.name]: v })} data-testid={`toggle-${t.name}`} />
            </div>
          ))}
          <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-800">
            <Label>Notification email</Label>
            <Input type="email" value={form.notification_email || ""} onChange={(e) => setForm({ ...form, notification_email: e.target.value })} data-testid="notification-email" />
          </div>
          <div className="space-y-2">
            <Label>Log retention (days)</Label>
            <Input type="number" min={1} value={form.log_retention_days || 90} onChange={(e) => setForm({ ...form, log_retention_days: Number(e.target.value) })} data-testid="log-retention" />
          </div>
          <Button onClick={() => mut.mutate(form)} disabled={mut.isPending} className="bg-slate-900 hover:bg-slate-800 dark:bg-blue-600 text-white font-semibold" data-testid="notifications-save">
            {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-2" /> Save Preferences</>)}
          </Button>
        </div>

        {/* Activity log */}
        <div className="lg:col-span-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm" data-testid="activity-log">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="h-4 w-4 text-blue-600" />
            <h3 className="font-display font-semibold text-slate-900 dark:text-slate-50">Activity & Logging Feed</h3>
          </div>
          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
            {logs.length === 0 ? (
              <p className="text-sm text-slate-400">No activity recorded.</p>
            ) : logs.map((l) => (
              <div key={l.id} className="flex items-start gap-3 pb-3 border-b border-slate-100 dark:border-slate-800 last:border-0" data-testid={`log-${l.id}`}>
                <StatusBadge status={l.level} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{l.event_type}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">{l.message}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{formatDateTime(l.timestamp)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
